// GISS map rotation for Leaflet (own implementation, replaces the GPL plugin leaflet-rotate).
// The map pane is rotated around the container centre with the CSS `rotate` property; Leaflet keeps
// working in unrotated layer coordinates. Container points (mouse, latLngToContainerPoint) are screen
// coordinates. Standard markers, tooltips and popups stay upright; custom panes rotate with the map.
import L from 'leaflet';

const RAD = Math.PI / 180;
const rot = (x, y, deg) => { const a = deg * RAD, c = Math.cos(a), s = Math.sin(a); return L.point(x * c - y * s, x * s + y * c); };
const UPRIGHT = ['markerPane', 'shadowPane'];
// text labels in rotating custom panes stay readable as well
const LABELS = '.pit-label, .ds-note, .cad-txt';
const brgOf = (el) => +(el?.closest?.('.leaflet-map-pane')?.dataset.brg || 0);

const { containerPointToLayerPoint: cp2lp, layerPointToContainerPoint: lp2cp } = L.Map.prototype;

L.Map.include({
  getBearing() { return this._gissBrg || 0; },
  setBearing(deg) {
    const b = ((Math.round(+deg * 10) / 10) % 360 + 360) % 360;
    if (!Number.isFinite(b) || b === this.getBearing()) return this;
    this._gissBrg = b;
    this._gissApplyRot();
    this.eachLayer((l) => { if (l._icon || l._container) (l.update || l._updatePosition)?.call(l); if (l._update && (l instanceof L.GridLayer || l instanceof L.Renderer)) l._update(); });
    return this.fire('rotate', { bearing: b });
  },
  _gissApplyRot() {
    const mp = this._mapPane, b = this.getBearing(), s = this.getSize();
    if (!mp) return;
    mp.style.transformOrigin = `${s.x / 2}px ${s.y / 2}px`;
    mp.style.rotate = b ? `${b}deg` : '';
    mp.dataset.brg = String(b);
  },
  containerPointToLayerPoint(point) {
    const b = this.getBearing();
    if (!b) return cp2lp.call(this, point);
    const p = L.point(point), c = this.getSize().divideBy(2);
    return cp2lp.call(this, rot(p.x - c.x, p.y - c.y, -b)._add(c));
  },
  layerPointToContainerPoint(point) {
    const b = this.getBearing(), p = lp2cp.call(this, L.point(point));
    if (!b) return p;
    const c = this.getSize().divideBy(2);
    return rot(p.x - c.x, p.y - c.y, b)._add(c);
  },
  // half diagonal of the view: area that must be covered by tiles / vectors while rotated
  _gissHalf() { const s = this.getSize(), d = Math.ceil(Math.hypot(s.x, s.y) / 2); return this.getBearing() ? L.point(d, d) : s.divideBy(2); },
});

L.Map.addInitHook(function () {
  if (!this.options.rotate) return;
  this._gissBrg = 0;
  this.on('resize', this._gissApplyRot, this);
  this.whenReady(() => { this._gissApplyRot(); if (this.options.bearing) this.setBearing(this.options.bearing); });
});

// tiles and vector renderers cover the rotated corners
const tiled = L.GridLayer.prototype._getTiledPixelBounds;
L.GridLayer.include({
  _getTiledPixelBounds(center) {
    const map = this._map;
    if (!map.getBearing()) return tiled.call(this, center);
    const z = map._animatingZoom ? Math.max(map._animateToZoom, map.getZoom()) : map.getZoom();
    const pc = map.project(center, this._tileZoom).floor(), half = map._gissHalf().divideBy(map.getZoomScale(z, this._tileZoom));
    return L.bounds(pc.subtract(half), pc.add(half));
  },
});
L.Renderer.include({
  _update() {
    const map = this._map, p = this.options.padding, half = map._gissHalf().multiplyBy(1 + p * 2);
    const c = cp2lp.call(map, map.getSize().divideBy(2));
    this._bounds = L.bounds(c.subtract(half).round(), c.add(half).round());
    this._center = map.getCenter();
    this._zoom = map.getZoom();
  },
  // zoom animation / zoom end: same half size as the bounds above, otherwise vectors shift while rotated
  _updateTransform(center, zoom) {
    const map = this._map, scale = map.getZoomScale(zoom, this._zoom), viewHalf = map._gissHalf().multiplyBy(1 + this.options.padding * 2);
    const off = viewHalf.multiplyBy(-scale).add(map.project(this._center, zoom)).subtract(map._getNewPixelOrigin(center, zoom));
    if (L.Browser.any3d) L.DomUtil.setTransform(this._container, off, scale); else L.DomUtil.setPosition(this._container, off);
  },
});

// upright standard markers / tooltips / popups: counter-rotation appended after Leaflet's translate3d
const upright = (el, b, origin) => { if (!el) return; el.style.transformOrigin = b ? origin : ''; if (b && !/rotate\(/.test(el.style.transform)) el.style.transform += ` rotate(${-b}deg)`; };
const mSet = L.Marker.prototype._setPos;
L.Marker.include({
  _setPos(pos) {
    mSet.call(this, pos);
    const b = this._map?.getBearing() || 0;
    if (!this._icon || !(UPRIGHT.includes(this.options.pane) || this._icon.matches(LABELS) || this._icon.querySelector(LABELS))) return;
    const st = this._icon.style;
    upright(this._icon, b, `${-parseFloat(st.marginLeft) || 0}px ${-parseFloat(st.marginTop) || 0}px`);
  },
});
const tSet = L.Tooltip.prototype._setPosition;
L.Tooltip.include({
  _setPosition(pos) {
    tSet.call(this, pos);
    const b = this._map?.getBearing() || 0, f = b && this._container && L.DomUtil.getPosition(this._container), cs = f && getComputedStyle(this._container);
    if (f) upright(this._container, b, `${pos.x - f.x - (parseFloat(cs.marginLeft) || 0)}px ${pos.y - f.y - (parseFloat(cs.marginTop) || 0)}px`);
    else if (this._container) upright(this._container, 0, '');
  },
});
const pSet = L.Popup.prototype._updatePosition;
L.Popup.include({ _updatePosition() { pSet.call(this); if (this._map) upright(this._container, this._map.getBearing(), `${-(this._containerLeft || 0)}px calc(100% + ${(this._containerBottom || 0) + (parseFloat(getComputedStyle(this._container).marginBottom) || 0)}px)`); } });

// dragging (map pan, marker drag): screen movement -> unrotated pane movement
const proxyEv = (e, b) => {
  const map1 = (t) => { const p = rot(t.clientX, t.clientY, -b); return new Proxy(t, { get: (o, k) => (k === 'clientX' ? p.x : k === 'clientY' ? p.y : typeof o[k] === 'function' ? o[k].bind(o) : o[k]) }); };
  const touches = e.touches && Array.from(e.touches, map1);
  return new Proxy(e, { get: (o, k) => (k === 'touches' ? touches : (k === 'clientX' || k === 'clientY') && !e.touches ? map1(e)[k] : typeof o[k] === 'function' ? o[k].bind(o) : o[k]) });
};
['_onDown', '_onMove'].forEach((fn) => {
  const orig = L.Draggable.prototype[fn];
  L.Draggable.prototype[fn] = function (e) { const b = brgOf(this._element); return orig.call(this, b && e ? proxyEv(e, b) : e); };
});

// pinch zoom: the pinch offset is measured on screen, the map moves in unrotated pixels
const tzMove = L.Map.TouchZoom.prototype._onTouchMove;
L.Map.TouchZoom.include({
  _onTouchMove(e) {
    const map = this._map, b = map.getBearing();
    if (!b || map.options.touchZoom === 'center' || !e.touches || e.touches.length !== 2 || !this._zooming) return tzMove.call(this, e);
    const p1 = map.mouseEventToContainerPoint(e.touches[0]), p2 = map.mouseEventToContainerPoint(e.touches[1]);
    const scale = p1.distanceTo(p2) / this._startDist;
    this._zoom = map.getScaleZoom(scale, this._startZoom);
    if (!map.options.bounceAtZoomLimits && ((this._zoom < map.getMinZoom() && scale < 1) || (this._zoom > map.getMaxZoom() && scale > 1))) this._zoom = map._limitZoom(this._zoom);
    const d = p1._add(p2)._divideBy(2)._subtract(this._centerPoint), delta = rot(d.x, d.y, -b);
    if (scale === 1 && d.x === 0 && d.y === 0) return;
    this._center = map.unproject(map.project(this._pinchStartLatLng, this._zoom).subtract(delta), this._zoom);
    if (!this._moved) { map._moveStart(true, false); this._moved = true; }
    L.Util.cancelAnimFrame(this._animRequest);
    this._animRequest = L.Util.requestAnimFrame(L.Util.bind(map._move, map, this._center, this._zoom, { pinch: true, round: false }, undefined), this, true);
    L.DomEvent.preventDefault(e);
  },
});
